import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, BarChart3, Users, Database, ServerCog, Sparkles, Gauge } from "lucide-react";
import Navbar from "@/components/Navbar";
import QuickAssessmentDialog from "@/components/QuickAssessmentDialog";

const CATEGORIES = [
  { name: "Program Governance", icon: BarChart3, desc: "Decision rights, cadence, and escalation paths that hold programs steady." },
  { name: "Stakeholder Alignment", icon: Users, desc: "Shared objectives and active sponsorship across business, IT, and ops." },
  { name: "Digital Systems Maturity", icon: Database, desc: "Integrated workflows that employees can actually use end-to-end." },
  { name: "Legacy System Risk", icon: ServerCog, desc: "Visibility into aging systems and the tribal knowledge holding them up." },
  { name: "AI & Automation Readiness", icon: Sparkles, desc: "Use cases tied to outcomes, with the data and posture to deliver." },
  { name: "Execution Capacity", icon: Gauge, desc: "The capacity, skills, and change muscle to land complex work." },
];

const Landing = () => {
  const navigate = useNavigate();
  const [quickOpen, setQuickOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute inset-0 hero-radial" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-32 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-600 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#75A1A2]" />
                Enterprise Alignment Readiness Assessment
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-900 leading-[1.05]">
                Find out where your <span className="text-[#75A1A2]">transformation</span> is quietly losing speed.
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                A 12-question diagnostic for senior leaders. Scored across six dimensions of enterprise alignment, with a tailored executive readiness report delivered on-screen. No fluff. No sales pitch. Twelve minutes.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate("/assessment")}
                  data-testid="hero-start-assessment-btn"
                  className="bg-[#312D44] hover:bg-[#1F1B30] text-white rounded-md px-6 py-6 text-base font-medium h-auto group"
                >
                  Start the assessment
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </Button>
                <Button
                  onClick={() => setQuickOpen(true)}
                  data-testid="hero-quick-assessment-btn"
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-white rounded-md px-6 py-6 text-base font-medium h-auto"
                >
                  Try the 60-second pulse
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#75A1A2]" /> 12 questions</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#75A1A2]" /> ~10 minutes</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#75A1A2]" /> Instant on-screen results</div>
              </div>
            </div>

            {/* Preview Card */}
            <div className="lg:col-span-5">
              <div className="relative bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_2px_24px_-8px_rgba(15,23,42,0.12)]">
                <div className="absolute -top-3 left-6 px-2 py-1 rounded-md bg-slate-900 text-white text-[10px] font-semibold tracking-wider uppercase">Sample Output</div>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="text-xs font-semibold text-slate-500 tracking-widest uppercase">Overall Readiness</div>
                  <div className="text-xs text-slate-400">Anonymized</div>
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="font-display text-5xl font-bold text-slate-900">62</span>
                  <span className="text-slate-400 text-lg">/100</span>
                </div>
                <div className="inline-flex items-center gap-2 mt-3 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
                  Operationally Ready
                </div>
                <div className="mt-6 space-y-3">
                  {[
                    ["Program Governance", 78, "bg-blue-600"],
                    ["Stakeholder Alignment", 64, "bg-blue-600"],
                    ["Digital Systems", 58, "bg-blue-600"],
                    ["Legacy Risk", 42, "bg-amber-500"],
                    ["AI Readiness", 70, "bg-blue-600"],
                    ["Execution Capacity", 60, "bg-blue-600"],
                  ].map(([cat, val, color]) => (
                    <div key={cat}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-600 font-medium">{cat}</span>
                        <span className="text-slate-900 font-semibold">{val}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${color} rounded-full`} style={{ width: `${val}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-500 mb-1">Recommended</div>
                  <div className="text-sm font-semibold text-slate-900">Legacy Workflow Risk Review</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="py-20 lg:py-28 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500 mb-3">What we measure</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Six dimensions that determine whether complex work actually lands.</h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">Every initiative depends on the same underlying conditions. We score yours against them.</p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((c) => (
              <div
                key={c.name}
                data-testid={`category-card-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group p-6 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-[0_2px_16px_-4px_rgba(15,23,42,0.08)] transition-all"
              >
                <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center mb-4">
                  <c.icon className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-1.5">{c.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500 mb-3">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">From twelve questions to a real conversation.</h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", title: "Answer twelve questions", body: "A 1–5 scale across six dimensions. Honest beats optimistic." },
              { n: "02", title: "See your scored readiness on-screen", body: "Math-driven scoring, then an executive narrative interpreting what your numbers mean — right after you submit." },
              { n: "03", title: "Discuss it with us", body: "Book a 30-minute Enterprise Alignment Review to talk through the result and the recommended next move." },
            ].map((s) => (
              <div key={s.n} className="p-6 bg-white border border-slate-200 rounded-xl">
                <div className="font-mono text-xs text-slate-400 mb-3">{s.n}</div>
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-2xl bg-[#312D44] px-8 py-14 sm:px-14 sm:py-16">
            <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
              backgroundSize: "32px 32px"
            }} />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white max-w-2xl">Twelve minutes today, twelve months of clarity ahead.</h2>
              <p className="mt-4 text-slate-300 max-w-xl">Run the assessment, get your scored report, and walk into your next leadership conversation with something specific to act on.</p>
              <div className="mt-8 flex gap-3 flex-wrap">
                <Button
                  onClick={() => navigate("/assessment")}
                  data-testid="cta-start-assessment-btn"
                  className="bg-white hover:bg-slate-100 text-slate-900 rounded-md px-6 py-6 text-base font-medium h-auto"
                >
                  Start the assessment <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} The River Company. Enterprise alignment & transformation.</div>
          <div className="flex gap-6">
            <Link to="/assessment" className="hover:text-slate-900">Assessment</Link>
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/admin" className="hover:text-slate-900">Admin</Link>
          </div>
        </div>
      </footer>

      <QuickAssessmentDialog open={quickOpen} onOpenChange={setQuickOpen} />
    </div>
  );
};

export default Landing;
